import { useEffect } from 'react'
import { Badge, Group, Paper, PasswordInput, Stack, Text } from '@mantine/core'
import { IconShieldCheck } from '@tabler/icons-react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { SaveFooter } from '@/components/ui/SaveFooter'
import { useChangePassword } from '@/hooks/useMyProfile'

// The length rule is the middleware's (and Medplum's); the confirmation is the form's own.
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
type PasswordFormValues = z.infer<typeof passwordSchema>

const EMPTY: PasswordFormValues = { currentPassword: '', newPassword: '', confirmPassword: '' }

// The fields a middleware refusal can name — the request's own two.
const REFUSABLE_FIELDS = ['currentPassword', 'newPassword'] as const

/**
 * The field a change-password refusal belongs to, from the error's `data.field`. Read
 * duck-typed: oRPC's own type definition warns `instanceof ORPCError` does not survive
 * bundle boundaries.
 */
function refusedField(error: unknown): (typeof REFUSABLE_FIELDS)[number] | undefined {
  const field = (error as { data?: { field?: unknown } } | null)?.data?.field
  return REFUSABLE_FIELDS.find((candidate) => candidate === field)
}

/**
 * Security: the two-factor card and the change-password form. Two-factor authentication is
 * mandatory for every user and cannot be turned off, so that card is static copy with no
 * toggle. The password form asks for the current password, a new one and its confirmation;
 * a refusal from the server lands on the field it names. A successful change clears the
 * form and leaves the session as it is.
 */
export function SecurityTab({
  onDirtyChange,
}: {
  /** Tells the page whether the tab holds unsaved edits (the tab dot, the reload warning). */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: EMPTY,
  })
  const change = useChangePassword()
  const { isDirty } = form.formState

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const save = async (values: PasswordFormValues) => {
    try {
      await change.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      form.reset(EMPTY)
    } catch (error) {
      const field = refusedField(error)
      if (field) {
        form.setError(field, { message: error instanceof Error ? error.message : 'Refused' })
      }
      // Any other failure is the footer's to show; the edits stay for a retry.
    }
  }

  const cancel = () => {
    form.reset(EMPTY)
    change.reset()
  }

  const saveError =
    change.error && !refusedField(change.error)
      ? { title: 'Password not changed', message: change.error.message }
      : null

  return (
    <form onSubmit={form.handleSubmit(save)} noValidate>
      <Stack gap="sm">
        <Paper withBorder radius="md" p="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <IconShieldCheck size={20} className="mt-0.5 shrink-0 text-[#275587]" />
              <Stack gap={2}>
                <Text fw={500} size="sm">
                  Two-factor authentication
                </Text>
                <Text size="xs" c="dimmed">
                  Required for all BeHuman users and cannot be disabled
                </Text>
              </Stack>
            </Group>
            <Badge size="sm" color="green" variant="light">
              Required and enabled
            </Badge>
          </Group>
        </Paper>

        <fieldset disabled={change.isPending} className="m-0 min-w-0 border-0 p-0">
          <Stack gap="sm">
            <Text fw={500} size="sm">
              Change password
            </Text>
            <Controller
              name="currentPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  label="Current password"
                  autoComplete="current-password"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="newPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  label="New password"
                  description="At least 8 characters"
                  autoComplete="new-password"
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  label="Confirm new password"
                  autoComplete="new-password"
                  error={fieldState.error?.message}
                />
              )}
            />
          </Stack>
        </fieldset>
      </Stack>
      <SaveFooter
        saveError={saveError}
        isSaving={change.isPending}
        isDirty={isDirty}
        onCancel={cancel}
      />
    </form>
  )
}
